import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { FilterQueryProps } from '../../../../global/filter/FilterQueryModal'
import { useFormatNumber } from '../../../../hooks/useFormatNumber'
import { WalletTypeEnum } from '../../../../model/WalletDto'
import { Tooltip } from '../../../../ui/Popups/Tooltip/Tooltip'
import { SummaryItem } from '../../../../ui/v2/TableSummary/TableSummary'
import { useAccountReadContext } from '../../../../utils/AccountContextContext'
import { useApiClient } from '../../../../utils/ApiClient'
import { ClientApiClient } from '../../../../utils/clientApi'
import { useWindowResize } from '../../../../utils/domUtils'
import { placeholder } from '../../../../utils/placeholder'
import { useCallbackWithForceRefresh } from '../../../../utils/useCallbackWithForceRefresh'
import { useFetchOne } from '../../../../utils/useFetch'

interface IBRebateApprovalsSummary {
  summaryItems: SummaryItem[]
  forceRefresh: () => void
  isLoading: boolean
}

export const useIBRebateApprovalsSummary = (
  search?: FilterQueryProps
): IBRebateApprovalsSummary => {
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const { compactNumber, formatNumber, formatMoney } = useFormatNumber()
  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const { callback, forceRefresh: forceRefreshApprovalSummary } =
    useCallbackWithForceRefresh(async () => {
      if (!account?.clientIntroducingBroker?.id) {
        return null
      }

      return await apiClient.getRebateApprovalSummary(account.clientIntroducingBroker.id, {
        search,
      })
    }, [account?.clientIntroducingBroker?.id, apiClient, search])

  const { data: summaryData, isLoading } = useFetchOne(callback)

  const { callback: availableBalanceCb, forceRefresh: forceRefreshAvailableBalance } =
    useCallbackWithForceRefresh(
      async () => await apiClient.getWallets({ walletTypeId: WalletTypeEnum.IB }),
      [account?.clientIntroducingBroker?.id, apiClient]
    )

  const { data: availableBalance, isLoading: isAvailableBalanceLoading } =
    useFetchOne(availableBalanceCb)

  const approvalsTotalCommission = summaryData?.approvalsTotalCommission ?? 0
  const balanceOnWallet = availableBalance?.[0].balance ?? 0

  const formattedValue = (value: number) => {
    if (isMobile) {
      return (
        <Tooltip.New content={`$${formatNumber(value)}`} disabled={!value || value < 1_000_000}>
          ${value ? compactNumber(value, 7) : placeholder.dash(value)}
        </Tooltip.New>
      )
    }

    return formatMoney(value, 'USD', value > 0 ? 2 : 0)
  }

  const summaryItems = useMemo<SummaryItem[]>(() => {
    return [
      {
        title: t('IB.RebatesPage.ApprovalsPage.Your Commission'),
        value: formattedValue(approvalsTotalCommission),
      },
      {
        title: t('IB.RebatesPage.ApprovalsPage.Rebates to Approve'),
        value: summaryData?.rebatesToApprove || 0,
      },
      {
        title: t('IB.RebatesPage.ApprovalsPage.Rebates Paid'),
        value: summaryData?.paidRebates || 0,
      },
      {
        title: t('Wallet.Available Balance'),
        value: formattedValue(balanceOnWallet),
      },
    ]
  }, [summaryData, t, isMobile, approvalsTotalCommission, balanceOnWallet])

  return {
    summaryItems,
    forceRefresh: () => {
      forceRefreshApprovalSummary()
      forceRefreshAvailableBalance()
    },
    isLoading: isLoading || isAvailableBalanceLoading,
  }
}
